/**
 * Minimal, vendor-neutral ad-campaign tracking.
 *
 * Why this exists: we run a small number of awareness campaigns and need to
 * measure one thing — how many people complete an event **registration**
 * (which is free for the user; payment is a separate, optional step). This
 * module captures first-touch campaign attribution (UTM params) and forwards
 * conversion events to Google Analytics 4 (gtag), a GTM-style
 * `window.dataLayer`, and the Meta (Facebook/Instagram) Pixel (`fbq`).
 *
 * Each destination is gated by its own env var (`VITE_GA_MEASUREMENT_ID`,
 * `VITE_META_PIXEL_ID`). When neither is set, no tag is loaded and no network
 * call is made, so local/dev/CI builds stay inert.
 *
 * Consent gate (UK PECR / UK GDPR): GA4 + Meta Pixel are non-essential, so
 * nothing loads — and no attribution is stored — until the user accepts cookies
 * (see `@/lib/consent`). On accept, the consent banner calls `initAnalytics()`.
 *
 * The Meta Pixel is what lets Meta optimize ad delivery toward conversions and
 * report campaign performance: it fires `PageView` on load and the
 * `CompleteRegistration` standard event when a (free) registration completes.
 *
 * Anything beyond this (Conversions API / server-side events, advanced matching,
 * payment/purchase conversions, an admin dashboard, consent management) is
 * intentionally out of scope — see the "ad tracking" follow-up issue.
 */

import { hasConsent } from "./consent";

const ATTRIBUTION_KEY = "qr_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type UtmKey = (typeof UTM_KEYS)[number];

export interface Attribution {
  /** ISO timestamp of the first visit that carried campaign params. */
  first_seen: string;
  /** Path the user first landed on. */
  landing_path: string;
  /** document.referrer at first touch (may be empty). */
  referrer: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

type GtagFn = (...args: unknown[]) => void;
type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

const hasWindow = (): boolean => typeof window !== "undefined";

export function getAttribution(): Attribution | null {
  if (!hasWindow()) return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/** Drop stored first-touch attribution, e.g. when the user withdraws consent. */
export function clearAttribution(): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Parse UTM params from a query string and, if any are present and we haven't
 * already stored a first touch, persist them as the first-touch attribution.
 * First-touch wins: later visits never overwrite the original campaign source.
 * Returns the effective stored attribution (existing or freshly captured).
 */
export function captureAttribution(
  search: string = hasWindow() ? window.location.search : "",
): Attribution | null {
  // Non-essential storage: don't write attribution until the user opts in.
  if (!hasConsent()) return null;
  const existing = getAttribution();
  if (existing) return existing;
  if (!hasWindow()) return null;

  const params = new URLSearchParams(search);
  const utm: Partial<Record<UtmKey, string>> = {};
  let hasAny = false;
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
      hasAny = true;
    }
  }
  if (!hasAny) return null;

  const attribution: Attribution = {
    first_seen: new Date().toISOString(),
    landing_path: window.location.pathname,
    referrer: document.referrer || "",
    ...utm,
  };
  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage unavailable (private mode / quota) — attribution is best-effort.
  }
  return attribution;
}

let initialized = false;

/**
 * Capture first-touch attribution and load any configured trackers (GA4 +
 * Meta Pixel). Safe to call once on app start; calling more than once is a
 * no-op.
 */
export function initAnalytics(): void {
  if (initialized || !hasWindow()) return;
  // GA4 + Meta Pixel are non-essential under UK PECR — load nothing until the
  // user has accepted. The consent banner calls this again on accept.
  if (!hasConsent()) return;
  initialized = true;

  // Capture first-touch attribution now that consent is granted.
  captureAttribution();

  initGa4();
  initMetaPixel();
}

function initGa4(): void {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) return;

  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", measurementId);
}

/**
 * Load the Meta (Facebook/Instagram) Pixel and record an initial PageView.
 * This is the browser tag Meta uses to optimize ad delivery and report
 * campaign performance. No-op unless `VITE_META_PIXEL_ID` is set.
 */
function initMetaPixel(): void {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (!pixelId) return;

  // Standard Meta Pixel bootstrap: define a stub that queues calls until the
  // real fbevents.js loads and replaces it.
  if (!window.fbq) {
    const fbq: FbqFn = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue!.push(args);
      }
    } as FbqFn;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = window._fbq || fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
}

/**
 * Forward a conversion/interaction event to GA4 (gtag) and to `window.dataLayer`
 * for GTM. No-op when no window is present. When no measurement id is set, the
 * dataLayer push still happens (harmless) but nothing is sent over the network.
 */
export function trackEvent(
  name: string,
  params: Record<string, unknown> = {},
): void {
  // Respect withdrawn consent: tags may still be loaded from earlier in the
  // session, so gate every forward — not just the initial load.
  if (!hasWindow() || !hasConsent()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...params, event: name });
  window.gtag?.("event", name, params);
}

export interface RegistrationConversion {
  event_id: number;
  quizrun_number?: number;
  city?: string;
  category: "solo" | "team";
}

/**
 * The single conversion that matters for our awareness campaigns: a user
 * completed a (free) event registration. Campaign source is attached from the
 * stored first-touch attribution so the conversion is self-describing even if
 * the ad platform's own attribution is unavailable.
 *
 * Also reported to the Meta Pixel as the `CompleteRegistration` standard event
 * — the conversion Meta optimizes delivery toward. Registration is free, so no
 * monetary `value`/`currency` is sent.
 */
export function trackRegistrationCompleted(conversion: RegistrationConversion): void {
  // Never report a conversion once consent is rejected/withdrawn, even if the
  // Pixel is still loaded from earlier in the session.
  if (!hasConsent()) return;
  const attribution = getAttribution();
  trackEvent("registration_completed", {
    ...conversion,
    ...(attribution ?? {}),
  });

  if (hasWindow()) {
    window.fbq?.("track", "CompleteRegistration", {
      content_name: conversion.city
        ? `Quizrun ${conversion.quizrun_number ?? ""} — ${conversion.city}`.trim()
        : "Quizrun registration",
      content_category: conversion.category,
    });
  }
}
